function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART9: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 136. Slack Standup Collector ─────────────────────────────────────

  "Slack Standup Collector": {
    nodes: [
      trigger("Schedule: Daily standup prompt at 9 AM", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Fires every weekday at 9 AM to kick off the daily standup collection process, targeting the configured team channel.",
        setup:
          "Set the schedule to 9 AM in your team's timezone. Configure the target Slack channel ID for standup prompts.",
        inputFields: [],
      }),
      slackNode(
        "2",
        "Slack: Post standup prompt to team channel",
        280,
        ":clipboard: *Daily Standup* — Please reply in a thread with:\n1. What did you accomplish yesterday?\n2. What are you working on today?\n3. Any blockers?\n\nPlease respond within the next hour.",
      ),
      n("3", "delayAction", "Wait 1 hour for team responses", 520, {
        delayDetails: { duration: 1, unit: "hours" },
      }),
      n(
        "4",
        "hitApi",
        "Slack: Collect thread replies from standup prompt",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://slack.com/api/conversations.replies?channel={channel_id}&ts={standup_thread_ts}",
            headers: [
              { key: "Authorization", value: "Bearer {slack_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "messages",
                customName: "standup_replies",
                type: "object",
              },
              {
                name: "messages.length",
                customName: "response_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "OpenAI: Summarize standup updates and identify blockers",
        1000,
        "You are a project management assistant. Summarize standup responses by person, highlight key accomplishments, today's priorities, and any blockers. Flag any team members who did not respond. Return summary, blockers, missing_members, highlight.",
        "Summarize these standup replies for the daily digest: {standup_replies}",
        [
          { field: "summary", as: "standup_summary", type: "string" },
          { field: "blockers", as: "blockers_list", type: "string" },
          { field: "missing_members", as: "missing_members", type: "string" },
          { field: "highlight", as: "key_highlight", type: "string" },
        ],
      ),
      n("6", "runCode", "Identify blockers requiring escalation", 1240, {
        runCodeDetails: {
          code: "const blockers = inputs.blockers_list; const hasBlockers = blockers && blockers.trim() !== '' && blockers.toLowerCase() !== 'none'; output = { has_blockers: hasBlockers, blocker_count: hasBlockers ? blockers.split('\\n').filter(b => b.trim()).length : 0, escalation_needed: hasBlockers };",
          outputs: [
            { name: "has_blockers", type: "boolean" },
            { name: "blocker_count", type: "number" },
            { name: "escalation_needed", type: "boolean" },
          ],
        },
      }),
      n(
        "7",
        "hitApi",
        "Notion: Create standup record with summary and blockers",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Notion-Version", value: "2022-06-28" },
            ],
            body: [
              { key: "parent", value: '{"database_id":"{standup_db_id}"}' },
              {
                key: "properties",
                value:
                  '{"Date":{"date":{"start":"{today_date}"}},"Summary":{"rich_text":[{"text":{"content":"{standup_summary}"}}]},"Blockers":{"rich_text":[{"text":{"content":"{blockers_list}"}}]},"Response Count":{"number":{response_count}}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Send standup digest to engineering manager",
        1720,
        "{manager_email}",
        "Daily Standup Digest — {today_date}",
        "Hi,\n\nHere is today's standup summary:\n\n{standup_summary}\n\nBlockers:\n{blockers_list}\n\nMissing responses from: {missing_members}\n\nKey highlight: {key_highlight}\n\nResponses received: {response_count}\n\nFull standup record: https://notion.so/{notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Posts a daily standup prompt to Slack at 9 AM, waits one hour for replies, collects all thread responses, uses AI to summarize updates and identify blockers, logs the standup to Notion, and emails a digest to the engineering manager for visibility into team progress.",
  },

  // ─── 137. Slack Request Intake to Jira ────────────────────────────────

  "Slack Request Intake to Jira": {
    nodes: [
      trigger(
        "Slack webhook: Emoji reaction added to request message",
        "webhook_app",
        {
          source: "slack",
          event: "reaction_added",
          howItStarts:
            "Fires when a designated emoji reaction (e.g., :ticket:) is added to a Slack message, capturing the message text, channel, user, and timestamp.",
          setup:
            "Configure a Slack app with the reactions:read and conversations:history scopes. Set the trigger emoji (e.g., :ticket:) in Slack Event Subscriptions.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract request details from Slack thread",
        280,
        "You are a project intake analyst. Extract the request title, description, requester name, urgency indicators, and any mentioned deadlines from the Slack message. Return title, description, requester, urgency, deadline_mentioned.",
        "Extract a structured request from this Slack message for Jira ticket creation: {webhook_payload}",
        [
          { field: "title", as: "request_title", type: "string" },
          { field: "description", as: "request_description", type: "string" },
          { field: "requester", as: "requester_name", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
          {
            field: "deadline_mentioned",
            as: "deadline_mentioned",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Classify priority and map to Jira fields", 520, {
        runCodeDetails: {
          code: "const urgency = inputs.urgency_level.toLowerCase(); let priority = 'Medium'; if (urgency.includes('critical') || urgency.includes('urgent') || urgency.includes('asap')) priority = 'High'; else if (urgency.includes('low') || urgency.includes('nice to have')) priority = 'Low'; const jiraPriority = priority === 'High' ? '2' : priority === 'Low' ? '4' : '3'; output = { priority_name: priority, jira_priority_id: jiraPriority };",
          outputs: [
            { name: "priority_name", type: "string" },
            { name: "jira_priority_id", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "Jira: Create issue from Slack request", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{project_key}"},"issuetype":{"name":"Task"},"summary":"{request_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"{request_description}\\n\\nRequested by: {requester_name} via Slack\\nDeadline: {deadline_mentioned}"}]}]},"priority":{"id":"{jira_priority_id}"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_issue_key", type: "string" },
            { name: "self", customName: "jira_issue_url", type: "string" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Reply in thread with Jira ticket link",
        1000,
        ":white_check_mark: Jira ticket created: *{jira_issue_key}* — {request_title}\nPriority: {priority_name}\nView: https://{jira_domain}.atlassian.net/browse/{jira_issue_key}",
      ),
      slackNode(
        "6",
        "Slack: DM assignee with ticket details",
        1240,
        ":incoming_envelope: New request assigned to you: *{jira_issue_key}*\n\n*{request_title}*\n{request_description}\n\nPriority: {priority_name}\nRequester: {requester_name}\nLink: https://{jira_domain}.atlassian.net/browse/{jira_issue_key}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log request intake details",
        1480,
        "SlackIntake",
        '[["{today_date}","{requester_name}","{request_title}","{priority_name}","{jira_issue_key}","{urgency_level}"]]',
      ),
      n("8", "runCode", "Track SLA timer and compute response deadline", 1720, {
        runCodeDetails: {
          code: "const priority = inputs.priority_name; let slaHours = 24; if (priority === 'High') slaHours = 4; else if (priority === 'Low') slaHours = 72; const deadline = new Date(Date.now() + slaHours * 3600000).toISOString(); output = { sla_hours: slaHours, sla_deadline: deadline };",
          outputs: [
            { name: "sla_hours", type: "number" },
            { name: "sla_deadline", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures Slack messages marked with a ticket emoji, uses AI to extract and structure the request, classifies priority, creates a Jira issue, replies in the Slack thread with the ticket link, DMs the assignee, logs to Google Sheets, and computes an SLA deadline based on priority.",
  },

  // ─── 138. Asana Task Completed Notifier ───────────────────────────────

  "Asana Task Completed Notifier": {
    nodes: [
      trigger("Asana webhook: Task marked as completed", "webhook_app", {
        source: "asana",
        event: "task.completed",
        howItStarts:
          "Fires when an Asana task is marked as completed, providing the task ID, name, assignee, project, and completion timestamp.",
        setup:
          "Register an Asana webhook on the target project. Ensure the webhook subscription includes the 'changed' action for task completion events.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract task details and determine stakeholder impact",
        280,
        "You are a project management analyst. Extract the task name, project name, assignee, completion date, any subtask count, and determine if this task has cross-team dependencies or stakeholders who need notification. Return task_name, project_name, assignee_name, completed_date, has_dependencies, stakeholder_teams.",
        "Analyze this Asana task completion event and identify stakeholders: {webhook_payload}",
        [
          { field: "task_name", as: "task_name", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "assignee_name", as: "assignee_name", type: "string" },
          { field: "completed_date", as: "completed_date", type: "string" },
          {
            field: "has_dependencies",
            as: "has_dependencies",
            type: "boolean",
          },
          {
            field: "stakeholder_teams",
            as: "stakeholder_teams",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine notification targets and compute velocity",
        520,
        {
          runCodeDetails: {
            code: "const hasDeps = inputs.has_dependencies; const teams = inputs.stakeholder_teams; const targets = teams ? teams.split(',').map(t => t.trim()) : []; output = { notify_count: targets.length, notify_list: targets.join(', '), needs_cross_team: hasDeps && targets.length > 0 };",
            outputs: [
              { name: "notify_count", type: "number" },
              { name: "notify_list", type: "string" },
              { name: "needs_cross_team", type: "boolean" },
            ],
          },
        },
      ),
      slackNode(
        "4",
        "Slack: Post task completion to project channel",
        760,
        ":white_check_mark: *Task Completed*\n*{task_name}* in {project_name}\nCompleted by: {assignee_name}\nDate: {completed_date}",
      ),
      n("5", "condition", "Check: Has cross-team stakeholders?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{needs_cross_team}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Notify cross-team stakeholders of completion",
        1240,
        "{stakeholder_emails}",
        "Task Completed: {task_name} in {project_name}",
        "Hi,\n\nA task that impacts your team has been completed:\n\n- Task: {task_name}\n- Project: {project_name}\n- Completed by: {assignee_name}\n- Date: {completed_date}\n\nIf this unblocks any of your pending work, please proceed accordingly.\n\nStakeholder teams notified: {notify_list}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log task completion for velocity tracking",
        1480,
        "TaskCompletions",
        '[["{completed_date}","{project_name}","{task_name}","{assignee_name}","{needs_cross_team}","{notify_count}"]]',
      ),
      n("8", "runCode", "Update team velocity metrics", 1720, {
        runCodeDetails: {
          code: "const today = new Date().toISOString().slice(0, 10); output = { velocity_date: today, task_completed: true, cross_team_notified: inputs.needs_cross_team };",
          outputs: [
            { name: "velocity_date", type: "string" },
            { name: "task_completed", type: "boolean" },
            { name: "cross_team_notified", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Listens for Asana task completions, uses AI to extract details and identify cross-team stakeholders, posts a completion notice to Slack, conditionally emails stakeholder teams if there are dependencies, logs the completion to Google Sheets for velocity tracking, and updates team metrics.",
  },

  // ─── 139. Trello Card from Airtable Record ────────────────────────────

  "Trello Card from Airtable Record": {
    nodes: [
      trigger(
        "Airtable webhook: New record created in project tracker",
        "webhook_app",
        {
          source: "airtable",
          event: "record.created",
          howItStarts:
            "Fires when a new record is created in the Airtable project tracker base, providing all field values including title, description, priority, owner, and due date.",
          setup:
            "Configure an Airtable automation webhook on record creation in the target base/table. Map the relevant fields in the webhook payload.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract card fields and format for Trello",
        280,
        "You are a project management assistant. Extract the card title, description, priority label, assignee, due date, and any checklist items from the Airtable record. Map priority to Trello label colors: High=red, Medium=yellow, Low=green. Return card_title, card_description, label_color, assignee, due_date, checklist_items.",
        "Map this Airtable record to Trello card fields: {webhook_payload}",
        [
          { field: "card_title", as: "card_title", type: "string" },
          { field: "card_description", as: "card_description", type: "string" },
          { field: "label_color", as: "label_color", type: "string" },
          { field: "assignee", as: "assignee_name", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "checklist_items", as: "checklist_items", type: "string" },
        ],
      ),
      n("3", "runCode", "Map Airtable fields to Trello API payload", 520, {
        runCodeDetails: {
          code: "const listMap = { 'High': '{trello_urgent_list_id}', 'Medium': '{trello_todo_list_id}', 'Low': '{trello_backlog_list_id}' }; const color = inputs.label_color; const listId = color === 'red' ? listMap.High : color === 'green' ? listMap.Low : listMap.Medium; output = { target_list_id: listId, formatted_desc: inputs.card_description + '\\n\\nAssignee: ' + inputs.assignee_name + '\\nSource: Airtable' };",
          outputs: [
            { name: "target_list_id", type: "string" },
            { name: "formatted_desc", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "Trello: Search for duplicate card by title", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.trello.com/1/search?query={card_title}&modelTypes=cards&board_stars=true&key={trello_key}&token={trello_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "cards", customName: "existing_cards", type: "object" },
            { name: "cards.length", customName: "match_count", type: "number" },
          ],
        },
      }),
      n("5", "condition", "Check: Duplicate card already exists?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{match_count}", operator: ">", value: "0" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Trello: Create new card with labels and due date",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.trello.com/1/cards?key={trello_key}&token={trello_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "name", value: "{card_title}" },
              { key: "desc", value: "{formatted_desc}" },
              { key: "idList", value: "{target_list_id}" },
              { key: "due", value: "{due_date}" },
            ],
            variables: [
              { name: "id", customName: "trello_card_id", type: "string" },
              {
                name: "shortUrl",
                customName: "trello_card_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "7",
        "hitApi",
        "Airtable: Write back Trello card link to source record",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.airtable.com/v0/{airtable_base_id}/{table_name}/{record_id}",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"Trello Link":"{trello_card_url}","Sync Status":"Synced"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_record_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log Airtable-to-Trello sync event",
        1720,
        "SyncLog",
        '[["{today_date}","{card_title}","{assignee_name}","{label_color}","{trello_card_id}","{updated_record_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Watches for new Airtable records in a project tracker, uses AI to map fields to Trello card format, checks for duplicate cards, creates a new Trello card with priority labels and due date if no duplicate exists, writes the Trello link back to Airtable, and logs the sync event to Google Sheets.",
  },

  // ─── 140. ClickUp Task to Google Calendar ─────────────────────────────

  "ClickUp Task to Google Calendar": {
    nodes: [
      trigger(
        "ClickUp webhook: Task created or updated with due date",
        "webhook_app",
        {
          source: "clickup",
          event: "taskUpdated",
          howItStarts:
            "Fires when a ClickUp task is created or updated with a due date, providing task ID, name, description, assignees, due date, priority, and list name.",
          setup:
            "Create a ClickUp webhook for the target workspace. Subscribe to taskCreated and taskUpdated events. The workflow filters for tasks with due dates.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract task details and format calendar event",
        280,
        "You are a productivity assistant. Extract the task name, description, due date, due time (if set), duration estimate, assignee names, priority, and project/list name from the ClickUp webhook. Format a calendar event title and description. Return task_name, event_title, event_description, start_datetime, end_datetime, assignees, priority.",
        "Extract ClickUp task details for a Google Calendar event: {webhook_payload}",
        [
          { field: "task_name", as: "task_name", type: "string" },
          { field: "event_title", as: "event_title", type: "string" },
          {
            field: "event_description",
            as: "event_description",
            type: "string",
          },
          { field: "start_datetime", as: "start_datetime", type: "string" },
          { field: "end_datetime", as: "end_datetime", type: "string" },
          { field: "assignees", as: "assignee_emails", type: "string" },
          { field: "priority", as: "priority_level", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Map ClickUp task to Google Calendar event payload",
        520,
        {
          runCodeDetails: {
            code: "const attendees = inputs.assignee_emails.split(',').map(e => ({ email: e.trim() })); const colorId = inputs.priority_level === 'urgent' ? '11' : inputs.priority_level === 'high' ? '4' : '1'; output = { attendees_json: JSON.stringify(attendees), color_id: colorId, has_time: inputs.start_datetime.includes('T') };",
            outputs: [
              { name: "attendees_json", type: "string" },
              { name: "color_id", type: "string" },
              { name: "has_time", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Calendar: Search for existing event by task ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/calendar/v3/calendars/{calendar_id}/events?q=ClickUp:{clickup_task_id}&maxResults=5",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "items", customName: "existing_events", type: "object" },
              {
                name: "items.length",
                customName: "event_match_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Calendar event already exists for this task?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{event_match_count}", operator: ">", value: "0" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Google Calendar: Create event with attendees and color coding",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/{calendar_id}/events",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "summary", value: "{event_title}" },
              {
                key: "description",
                value: "{event_description}\n\nClickUp:{clickup_task_id}",
              },
              { key: "start", value: '{"dateTime":"{start_datetime}"}' },
              { key: "end", value: '{"dateTime":"{end_datetime}"}' },
              { key: "colorId", value: "{color_id}" },
              { key: "attendees", value: "{attendees_json}" },
            ],
            variables: [
              { name: "id", customName: "calendar_event_id", type: "string" },
              {
                name: "htmlLink",
                customName: "calendar_event_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "7",
        "hitApi",
        "ClickUp: Write calendar sync status back to task",
        1480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://api.clickup.com/api/v2/task/{clickup_task_id}",
            headers: [
              { key: "Authorization", value: "{clickup_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "custom_fields",
                value:
                  '[{"id":"{gcal_sync_field_id}","value":"Synced: {calendar_event_id}"}]',
              },
            ],
            variables: [
              { name: "id", customName: "updated_task_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log calendar sync event",
        1720,
        "CalendarSync",
        '[["{today_date}","{task_name}","{priority_level}","{start_datetime}","{calendar_event_id}","{clickup_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors ClickUp for tasks with due dates, uses AI to extract and format calendar event details, checks Google Calendar for existing events, creates a color-coded calendar event with attendees if none exists, writes the sync status back to ClickUp, and logs to Google Sheets.",
  },

  // ─── 141. Google Calendar to Notion Sync ──────────────────────────────

  "Google Calendar to Notion Sync": {
    nodes: [
      trigger(
        "Google Calendar webhook: Event created or updated",
        "webhook_app",
        {
          source: "google_calendar",
          event: "event.updated",
          howItStarts:
            "Fires when a Google Calendar event is created or updated, providing the event ID, title, description, start/end times, attendees, and location.",
          setup:
            "Set up a Google Calendar push notification channel for the target calendar. Configure the webhook to receive event change notifications.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract event details and map to Notion properties",
        280,
        "You are a productivity sync assistant. Extract the event title, date, start time, end time, attendees list, location, description, meeting type (internal/external/1:1/team), and any agenda items. Return event_title, date, start_time, end_time, attendees, location, meeting_type, agenda, description.",
        "Map this Google Calendar event to a Notion meeting database entry: {webhook_payload}",
        [
          { field: "event_title", as: "event_title", type: "string" },
          { field: "date", as: "event_date", type: "string" },
          { field: "start_time", as: "start_time", type: "string" },
          { field: "end_time", as: "end_time", type: "string" },
          { field: "attendees", as: "attendees_list", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "agenda", as: "agenda_items", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Map event to Notion schema and build page payload",
        520,
        {
          runCodeDetails: {
            code: "const typeEmoji = { 'internal': '🏢', 'external': '🤝', '1:1': '👤', 'team': '👥' }; const emoji = typeEmoji[inputs.meeting_type] || '📅'; const attendeeCount = inputs.attendees_list ? inputs.attendees_list.split(',').length : 0; output = { type_emoji: emoji, attendee_count: attendeeCount, notion_status: 'Scheduled' };",
            outputs: [
              { name: "type_emoji", type: "string" },
              { name: "attendee_count", type: "number" },
              { name: "notion_status", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Notion: Search for existing page by calendar event ID",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/databases/{meetings_db_id}/query",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Notion-Version", value: "2022-06-28" },
            ],
            body: [
              {
                key: "filter",
                value:
                  '{"property":"Calendar ID","rich_text":{"equals":"{google_event_id}"}}',
              },
            ],
            variables: [
              { name: "results", customName: "existing_pages", type: "object" },
              {
                name: "results.length",
                customName: "page_match_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Notion page already exists for this event?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{page_match_count}", operator: ">", value: "0" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Notion: Create meeting page with properties and notes template",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Notion-Version", value: "2022-06-28" },
            ],
            body: [
              { key: "parent", value: '{"database_id":"{meetings_db_id}"}' },
              { key: "icon", value: '{"emoji":"{type_emoji}"}' },
              {
                key: "properties",
                value:
                  '{"Name":{"title":[{"text":{"content":"{event_title}"}}]},"Date":{"date":{"start":"{start_time}","end":"{end_time}"}},"Type":{"select":{"name":"{meeting_type}"}},"Attendees":{"rich_text":[{"text":{"content":"{attendees_list}"}}]},"Status":{"select":{"name":"Scheduled"}},"Calendar ID":{"rich_text":[{"text":{"content":"{google_event_id}"}}]}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
              { name: "url", customName: "notion_page_url", type: "string" },
            ],
          },
        },
      ),
      n(
        "7",
        "runCode",
        "Inject meeting notes template into Notion page",
        1480,
        {
          runCodeDetails: {
            code: "const template = '## Agenda\\n' + (inputs.agenda_items || 'TBD') + '\\n\\n## Notes\\n- \\n\\n## Action Items\\n- [ ] \\n\\n## Decisions\\n- '; output = { notes_template: template, template_injected: true };",
            outputs: [
              { name: "notes_template", type: "string" },
              { name: "template_injected", type: "boolean" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log calendar-to-Notion sync",
        1720,
        "CalNotionSync",
        '[["{today_date}","{event_title}","{event_date}","{meeting_type}","{attendee_count}","{notion_page_id}","{google_event_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Syncs Google Calendar events to Notion by extracting event details with AI, checking for existing Notion pages, creating a new meeting page with typed icons and structured properties, injecting a meeting notes template, and logging the sync to Google Sheets.",
  },

  // ─── 142. Notion Database to Google Sheet Sync ────────────────────────

  "Notion Database to Google Sheet Sync": {
    nodes: [
      trigger(
        "Schedule: Sync Notion database to Google Sheets every 30 min",
        "schedule",
        {
          schedule: "Every 30 minutes",
          howItStarts:
            "Fires every 30 minutes to pull recently modified pages from a Notion database and sync them to a Google Sheet for reporting and collaboration.",
          setup:
            "Configure the Notion database ID and Google Sheets spreadsheet ID. Ensure the Notion integration has read access to the target database.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Notion: Fetch recently modified pages from database",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/databases/{notion_db_id}/query",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Notion-Version", value: "2022-06-28" },
            ],
            body: [
              {
                key: "filter",
                value:
                  '{"timestamp":"last_edited_time","last_edited_time":{"after":"{last_sync_time}"}}',
              },
              {
                key: "sorts",
                value:
                  '[{"timestamp":"last_edited_time","direction":"descending"}]',
              },
            ],
            variables: [
              { name: "results", customName: "changed_pages", type: "object" },
              {
                name: "results.length",
                customName: "changed_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Flatten Notion properties to spreadsheet rows", 520, {
        runCodeDetails: {
          code: "const pages = inputs.changed_pages || []; const rows = pages.map(p => { const props = p.properties || {}; return [p.id, props.Name?.title?.[0]?.text?.content || '', props.Status?.select?.name || '', props.Priority?.select?.name || '', props.Assignee?.rich_text?.[0]?.text?.content || '', p.last_edited_time]; }); output = { flattened_rows: JSON.stringify(rows), row_count: rows.length };",
          outputs: [
            { name: "flattened_rows", type: "string" },
            { name: "row_count", type: "number" },
          ],
        },
      }),
      n(
        "4",
        "hitApi",
        "Google Sheets: Search for existing rows by Notion page ID",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/NotionSync!A:A",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "values", customName: "existing_ids", type: "object" },
              {
                name: "values.length",
                customName: "existing_row_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "runCode", "Determine create vs update for each row", 1000, {
        runCodeDetails: {
          code: "const existingIds = (inputs.existing_ids || []).flat(); const rows = JSON.parse(inputs.flattened_rows); const toCreate = rows.filter(r => !existingIds.includes(r[0])); const toUpdate = rows.filter(r => existingIds.includes(r[0])); output = { create_rows: JSON.stringify(toCreate), update_rows: JSON.stringify(toUpdate), create_count: toCreate.length, update_count: toUpdate.length };",
          outputs: [
            { name: "create_rows", type: "string" },
            { name: "update_rows", type: "string" },
            { name: "create_count", type: "number" },
            { name: "update_count", type: "number" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Google Sheets: Batch write new and updated rows",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/NotionSync!A1:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "values", value: "{create_rows}" }],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_written",
                type: "number",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Add sync timestamp to audit log",
        1480,
        "SyncAudit",
        '[["{today_date}","{changed_count}","{create_count}","{update_count}","{rows_written}"]]',
      ),
      n("8", "runCode", "Log sync health and set next sync checkpoint", 1720, {
        runCodeDetails: {
          code: "const now = new Date().toISOString(); const success = inputs.rows_written > 0 || inputs.changed_count === 0; output = { last_sync_time: now, sync_healthy: success, sync_status: success ? 'OK' : 'WARNING: rows_written mismatch' };",
          outputs: [
            { name: "last_sync_time", type: "string" },
            { name: "sync_healthy", type: "boolean" },
            { name: "sync_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Polls a Notion database every 30 minutes for changed pages, flattens properties to spreadsheet rows, checks Google Sheets for existing rows by ID, performs create-or-update batch writes, logs the sync to an audit sheet, and records health status for monitoring.",
  },

  // ─── 143. Google Sheet to Airtable Sync ───────────────────────────────

  "Google Sheet to Airtable Sync": {
    nodes: [
      trigger(
        "Google Sheets webhook: Row changed in source spreadsheet",
        "webhook_app",
        {
          source: "google_sheets",
          event: "row.changed",
          howItStarts:
            "Fires when a row is added or edited in a Google Sheet, providing the row number, cell values, sheet name, and change timestamp.",
          setup:
            "Configure an Apps Script trigger on the source Google Sheet to send a webhook on row edits. Include row data and sheet metadata in the payload.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract row data and map to Airtable field types",
        280,
        "You are a data integration specialist. Extract all field values from the Google Sheets row and map them to Airtable-compatible types. Handle dates, numbers, single-select, and multi-select conversions. Return row_id, fields (as key-value pairs), primary_key, record_name.",
        "Transform this Google Sheets row data into Airtable field mappings: {webhook_payload}",
        [
          { field: "row_id", as: "sheet_row_id", type: "string" },
          { field: "primary_key", as: "primary_key_value", type: "string" },
          { field: "record_name", as: "record_name", type: "string" },
          { field: "fields", as: "mapped_fields", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Transform data types for Airtable compatibility",
        520,
        {
          runCodeDetails: {
            code: "const fields = JSON.parse(inputs.mapped_fields || '{}'); const airtableFields = {}; for (const [key, val] of Object.entries(fields)) { if (val === '' || val === null) continue; airtableFields[key] = val; } output = { airtable_payload: JSON.stringify({ fields: airtableFields }), field_count: Object.keys(airtableFields).length };",
            outputs: [
              { name: "airtable_payload", type: "string" },
              { name: "field_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Airtable: Search for existing record by primary key",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.airtable.com/v0/{airtable_base_id}/{table_name}?filterByFormula=FIND(%22{primary_key_value}%22,{Primary Key})&maxRecords=1",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records",
                customName: "existing_records",
                type: "object",
              },
              {
                name: "records.length",
                customName: "record_match_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Record already exists in Airtable?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{record_match_count}", operator: ">", value: "0" },
          ],
        },
      }),
      n("6", "hitApi", "Airtable: Create new record or update existing", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/{table_name}",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "records", value: "[{airtable_payload}]" }],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_record_id",
              type: "string",
            },
          ],
        },
      }),
      n("7", "hitApi", "Airtable: Handle linked record relationships", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.airtable.com/v0/{airtable_base_id}/{table_name}/{airtable_record_id}",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"Sheet Row":"{sheet_row_id}","Last Synced":"{today_date}","Sync Source":"Google Sheets"}',
            },
          ],
          variables: [
            { name: "id", customName: "linked_record_id", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log sync to audit trail",
        1720,
        "AirtableSync",
        '[["{today_date}","{record_name}","{primary_key_value}","{airtable_record_id}","{field_count}","synced"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Catches Google Sheet row changes, uses AI to extract and map field types for Airtable compatibility, searches for existing records by primary key, creates or updates the Airtable record, links back to the source row, and logs the sync event for auditing.",
  },

  // ─── 144. Team Changelog from Slack Posts ─────────────────────────────

  "Team Changelog from Slack Posts": {
    nodes: [
      trigger(
        "Slack webhook: Message posted to #changelog channel",
        "webhook_app",
        {
          source: "slack",
          event: "message.channels",
          howItStarts:
            "Fires when a new message is posted to the designated #changelog Slack channel, capturing the message text, author, timestamp, and any thread replies.",
          setup:
            "Configure a Slack app with channels:history scope. Subscribe to the message.channels event for the #changelog channel only.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract change details, owner, and impact from post",
        280,
        "You are a changelog analyst. Extract the change title, description, owner/author, impacted systems or teams, change type (feature/bugfix/config/infra), severity (breaking/non-breaking), and any rollback instructions. Return change_title, description, owner, impacted_teams, change_type, is_breaking, rollback_notes.",
        "Parse this Slack changelog post into structured change record: {webhook_payload}",
        [
          { field: "change_title", as: "change_title", type: "string" },
          { field: "description", as: "change_description", type: "string" },
          { field: "owner", as: "change_owner", type: "string" },
          { field: "impacted_teams", as: "impacted_teams", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "is_breaking", as: "is_breaking", type: "boolean" },
          { field: "rollback_notes", as: "rollback_notes", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Categorize change type and determine notification scope",
        520,
        {
          runCodeDetails: {
            code: "const type = inputs.change_type.toLowerCase(); const category = type.includes('feature') ? 'Feature' : type.includes('bug') ? 'Bug Fix' : type.includes('config') ? 'Configuration' : type.includes('infra') ? 'Infrastructure' : 'Other'; const teams = inputs.impacted_teams.split(',').map(t => t.trim()); output = { category: category, team_count: teams.length, teams_formatted: teams.join(', '), notify_all: inputs.is_breaking || teams.length > 2 };",
            outputs: [
              { name: "category", type: "string" },
              { name: "team_count", type: "number" },
              { name: "teams_formatted", type: "string" },
              { name: "notify_all", type: "boolean" },
            ],
          },
        },
      ),
      n("4", "hitApi", "Notion: Create changelog entry with metadata", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{changelog_db_id}"}' },
            {
              key: "properties",
              value:
                '{"Title":{"title":[{"text":{"content":"{change_title}"}}]},"Type":{"select":{"name":"{category}"}},"Owner":{"rich_text":[{"text":{"content":"{change_owner}"}}]},"Breaking":{"checkbox":{is_breaking}},"Teams":{"rich_text":[{"text":{"content":"{teams_formatted}"}}]},"Date":{"date":{"start":"{today_date}"}}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_entry_id", type: "string" },
            { name: "url", customName: "notion_entry_url", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Check: Is this a breaking change?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{is_breaking}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Alert affected teams about breaking change",
        1240,
        "{affected_team_emails}",
        "Breaking Change Alert: {change_title}",
        "A breaking change has been deployed that affects your team:\n\n- Change: {change_title}\n- Description: {change_description}\n- Owner: {change_owner}\n- Type: {category}\n- Affected teams: {teams_formatted}\n\nRollback instructions:\n{rollback_notes}\n\nFull changelog entry: {notion_entry_url}",
      ),
      slackNode(
        "7",
        "Slack: Pin summary to #changelog thread",
        1480,
        ":memo: *Changelog Recorded*\n*{change_title}* ({category})\nOwner: {change_owner} | Breaking: {is_breaking}\nTeams: {teams_formatted}\nNotion: {notion_entry_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log changelog entry for reporting",
        1720,
        "Changelog",
        '[["{today_date}","{change_title}","{category}","{change_owner}","{is_breaking}","{teams_formatted}","{notion_entry_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Watches the #changelog Slack channel for new posts, uses AI to extract change details including type and breaking status, creates a structured Notion changelog entry, emails affected teams for breaking changes, pins a summary to Slack, and logs to Google Sheets for reporting.",
  },

  // ─── 145. Meeting Action Items Extractor ──────────────────────────────

  "Meeting Action Items Extractor": {
    nodes: [
      trigger("Zoom webhook: Meeting recording available", "webhook_app", {
        source: "zoom",
        event: "recording.completed",
        howItStarts:
          "Fires when a Zoom meeting recording is ready, providing the meeting ID, topic, recording URL, host email, participants, and duration.",
        setup:
          "Enable Zoom webhook notifications for recording.completed events. Ensure the Zoom app has recording:read scope and cloud recording is enabled.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Transcribe and parse meeting content",
        280,
        "You are a meeting transcription analyst. Process the meeting recording metadata and any available transcript. Extract the meeting title, date, duration, participant names, and a structured transcript summary. Return meeting_title, meeting_date, duration_min, participants, transcript_summary.",
        "Process this Zoom recording and extract meeting details: {webhook_payload}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "duration_min", as: "duration_min", type: "number" },
          { field: "participants", as: "participant_list", type: "string" },
          {
            field: "transcript_summary",
            as: "transcript_summary",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Extract action items with owners and deadlines",
        520,
        "You are a project management assistant specializing in meeting follow-ups. From the meeting transcript summary, extract every action item with its assigned owner, deadline (if mentioned), and priority (high/medium/low). Return action_items as a JSON array with fields: task, owner, deadline, priority.",
        "Extract action items from this meeting summary: {transcript_summary}. Participants: {participant_list}",
        [
          { field: "action_items", as: "action_items_json", type: "string" },
          { field: "item_count", as: "action_item_count", type: "number" },
          { field: "decisions", as: "key_decisions", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Validate assignees and format Asana task payloads",
        760,
        {
          runCodeDetails: {
            code: "const items = JSON.parse(inputs.action_items_json || '[]'); const validated = items.map(item => ({ ...item, owner: item.owner || 'Unassigned', deadline: item.deadline || 'TBD', priority: item.priority || 'medium' })); output = { validated_items: JSON.stringify(validated), total_tasks: validated.length, has_unassigned: validated.some(i => i.owner === 'Unassigned') };",
            outputs: [
              { name: "validated_items", type: "string" },
              { name: "total_tasks", type: "number" },
              { name: "has_unassigned", type: "boolean" },
            ],
          },
        },
      ),
      n("5", "hitApi", "Asana: Create tasks for each action item", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{action_item_title}","notes":"From meeting: {meeting_title}\\nDate: {meeting_date}\\nAssigned to: {item_owner}\\nPriority: {item_priority}","projects":["{asana_project_id}"],"due_on":"{item_deadline}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: DM each owner with their assigned action items",
        1240,
        ":clipboard: *Action Items from: {meeting_title}*\nDate: {meeting_date}\n\nYour tasks:\n{owner_tasks}\n\nAll action items tracked in Asana: {asana_project_url}\n\nDecisions made: {key_decisions}",
      ),
      emailNode(
        "7",
        "Email: Send full meeting summary with all action items",
        1480,
        "{all_participant_emails}",
        "Meeting Summary & Action Items: {meeting_title}",
        "Hi team,\n\nHere is the summary from our meeting on {meeting_date} ({duration_min} min):\n\n{transcript_summary}\n\nKey Decisions:\n{key_decisions}\n\nAction Items ({total_tasks} total):\n{validated_items}\n\nAll tasks are tracked in Asana. Please review your assignments.\n\nParticipants: {participant_list}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log meeting and action item metrics",
        1720,
        "MeetingActions",
        '[["{meeting_date}","{meeting_title}","{duration_min}","{participant_list}","{total_tasks}","{has_unassigned}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Zoom meeting recordings, uses AI to transcribe and extract action items with owners and deadlines, validates assignees, creates Asana tasks for each item, DMs owners via Slack, emails the full meeting summary to all participants, and logs metrics to Google Sheets.",
  },

  // ─── 146. Cross-Team Launch Checklist ──────────────────────────────────

  "Cross-Team Launch Checklist": {
    nodes: [
      trigger("Asana webhook: Launch project created", "webhook_app", {
        source: "asana",
        event: "project.added",
        howItStarts:
          "Fires when a new launch project is created in Asana with a 'Launch' tag, providing the project ID, name, owner, due date, and team assignments.",
        setup:
          "Register an Asana webhook on the workspace or team. Filter for projects with the 'Launch' tag or template. Ensure the integration has project write access.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Generate checklist items per team from launch template",
        280,
        {
          runCodeDetails: {
            code: "const teams = ['Engineering', 'Marketing', 'Sales', 'Support', 'Legal']; const checklists = teams.map(team => { const items = { 'Engineering': ['Feature freeze', 'QA sign-off', 'Staging deployment', 'Production deploy'], 'Marketing': ['Blog post draft', 'Social assets ready', 'Email campaign queued', 'Landing page live'], 'Sales': ['Battle cards updated', 'Demo script ready', 'Pricing confirmed', 'Enablement session scheduled'], 'Support': ['KB articles published', 'Runbook created', 'Team training done', 'Escalation path set'], 'Legal': ['Terms reviewed', 'Privacy impact assessed', 'Compliance sign-off'] }; return { team, tasks: items[team] || [] }; }); output = { checklists: JSON.stringify(checklists), total_tasks: checklists.reduce((sum, c) => sum + c.tasks.length, 0), team_count: teams.length };",
            outputs: [
              { name: "checklists", type: "string" },
              { name: "total_tasks", type: "number" },
              { name: "team_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Asana: Create sub-tasks for each team checklist item",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"{task_name}","notes":"Launch checklist item for {team_name}","projects":["{launch_project_id}"],"tags":["{team_tag_id}"],"due_on":"{launch_date}"}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "created_task_ids",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "4",
        "Slack: Notify team leads of launch checklist assignments",
        760,
        ":rocket: *Launch Checklist Created*\nProject: {launch_project_name}\nLaunch date: {launch_date}\n\n{team_count} teams assigned | {total_tasks} total tasks\n\nTeam leads: Please review your assigned items in Asana and confirm ownership.\n\nAsana project: {asana_project_url}",
      ),
      n(
        "5",
        "delayAction",
        "Wait for check interval (48 hours before launch)",
        1000,
        { delayDetails: { duration: 48, unit: "hours" } },
      ),
      n("6", "runCode", "Track task completion across all teams", 1240, {
        runCodeDetails: {
          code: "const completed = inputs.completed_count || 0; const total = inputs.total_tasks; const pct = Math.round((completed / total) * 100); const allDone = completed >= total; const atRisk = pct < 75; output = { completion_pct: pct, all_complete: allDone, at_risk: atRisk, remaining: total - completed };",
          outputs: [
            { name: "completion_pct", type: "number" },
            { name: "all_complete", type: "boolean" },
            { name: "at_risk", type: "boolean" },
            { name: "remaining", type: "number" },
          ],
        },
      }),
      n("7", "condition", "Check: All checklist items complete?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{all_complete}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Announce launch readiness or escalate blockers",
        1720,
        ":white_check_mark: *Launch Readiness Update*\nProject: {launch_project_name}\nCompletion: {completion_pct}% ({remaining} items remaining)\n\n{launch_status_message}\n\nAsana: {asana_project_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Generates a cross-team launch checklist when a launch project is created in Asana, creates sub-tasks for Engineering, Marketing, Sales, Support, and Legal teams, notifies team leads via Slack, waits for a check interval, tracks completion percentage, and announces readiness or escalates blockers.",
  },

  // ─── 147. OOO Request to Calendar Sync ────────────────────────────────

  "OOO Request to Calendar Sync": {
    nodes: [
      trigger("Slack webhook: Out-of-office request posted", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when a message containing an OOO request pattern is posted in the #time-off Slack channel, capturing the requester, dates, and coverage notes.",
        setup:
          "Configure a Slack webhook for the #time-off or #ooo channel. The workflow detects messages matching OOO patterns (dates, 'out of office', 'PTO', etc.).",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract OOO dates, coverage plan, and contact info",
        280,
        "You are an HR operations assistant. Extract the employee name, start date, end date, reason category (PTO/sick/personal/conference), coverage person, emergency contact, and any special notes from the OOO request. Return employee_name, start_date, end_date, reason, coverage_person, emergency_contact, notes.",
        "Parse this Slack OOO request into structured time-off details: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "start_date", as: "ooo_start", type: "string" },
          { field: "end_date", as: "ooo_end", type: "string" },
          { field: "reason", as: "ooo_reason", type: "string" },
          { field: "coverage_person", as: "coverage_person", type: "string" },
          {
            field: "emergency_contact",
            as: "emergency_contact",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Google Calendar: Create OOO block on team calendar",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/{team_calendar_id}/events",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "summary", value: "OOO: {employee_name} ({ooo_reason})" },
              {
                key: "description",
                value:
                  "Coverage: {coverage_person}\nEmergency: {emergency_contact}",
              },
              { key: "start", value: '{"date":"{ooo_start}"}' },
              { key: "end", value: '{"date":"{ooo_end}"}' },
              { key: "transparency", value: "opaque" },
            ],
            variables: [
              { name: "id", customName: "calendar_event_id", type: "string" },
              { name: "htmlLink", customName: "calendar_link", type: "string" },
            ],
          },
        },
      ),
      n("4", "hitApi", "Slack: Set user status to OOO with return date", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/users.profile.set",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile",
              value:
                '{"status_text":"OOO until {ooo_end} — contact {coverage_person}","status_emoji":":palm_tree:","status_expiration":"{ooo_end_timestamp}"}',
            },
          ],
          variables: [
            { name: "ok", customName: "status_set", type: "boolean" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Notify team of OOO and coverage arrangements",
        1000,
        "{team_email}",
        "{employee_name} OOO: {ooo_start} to {ooo_end}",
        "Hi team,\n\n{employee_name} will be out of office from {ooo_start} to {ooo_end} ({ooo_reason}).\n\nCoverage: {coverage_person}\nEmergency contact: {emergency_contact}\n\nCalendar block: {calendar_link}\n\nPlease direct any urgent matters to {coverage_person} during this period.",
      ),
      n("6", "runCode", "Update coverage schedule and compute days out", 1240, {
        runCodeDetails: {
          code: "const start = new Date(inputs.ooo_start); const end = new Date(inputs.ooo_end); const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)); output = { days_out: days, is_extended: days > 5, return_date: inputs.ooo_end };",
          outputs: [
            { name: "days_out", type: "number" },
            { name: "is_extended", type: "boolean" },
            { name: "return_date", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log OOO request for tracking",
        1480,
        "OOOLog",
        '[["{today_date}","{employee_name}","{ooo_start}","{ooo_end}","{days_out}","{ooo_reason}","{coverage_person}"]]',
      ),
      n(
        "8",
        "delayAction",
        "Wait until return date to reset Slack status",
        1720,
        { delayDetails: { duration: 1, unit: "days" } },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Parses OOO requests from Slack using AI, creates a calendar block on the team calendar, sets the employee's Slack status with coverage info, emails the team with coverage arrangements, logs the request to Google Sheets, and schedules a status reset for the return date.",
  },

  // ─── 148. Weekly Status Report Compiler ───────────────────────────────

  "Weekly Status Report Compiler": {
    nodes: [
      trigger(
        "Schedule: Compile weekly status every Friday at 4 PM",
        "schedule",
        {
          schedule: "Every Friday at 4:00 PM",
          howItStarts:
            "Fires every Friday at 4 PM to collect completed work from Asana and Jira, compile a team status report, and distribute to stakeholders.",
          setup:
            "Configure the Asana project IDs and Jira project keys to pull data from. Set the recipient email list for the weekly report.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Asana: Fetch tasks completed this week", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?project={asana_project_id}&completed_since={week_start_date}&opt_fields=name,assignee.name,completed_at,custom_fields",
          headers: [{ key: "Authorization", value: "Bearer {asana_token}" }],
          body: [],
          variables: [
            { name: "data", customName: "asana_completed", type: "object" },
            { name: "data.length", customName: "asana_count", type: "number" },
          ],
        },
      }),
      n("3", "hitApi", "Jira: Fetch issues resolved this week", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "jql",
              value:
                "project = {project_key} AND status changed to Done DURING (startOfWeek(), now()) ORDER BY resolved DESC",
            },
            {
              key: "fields",
              value:
                '["summary","assignee","resolutiondate","issuetype","priority"]',
            },
          ],
          variables: [
            { name: "issues", customName: "jira_resolved", type: "object" },
            { name: "total", customName: "jira_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Compile narrative summary by team",
        760,
        "You are a project reporting analyst. Compile a professional weekly status report from Asana tasks and Jira issues. Group by team/area, highlight key accomplishments, note any carryover items, and flag risks. Return executive_summary, accomplishments_by_team, risks, carryover_items, metrics_summary.",
        "Create a weekly status report from these completed items. Asana: {asana_completed}. Jira: {jira_resolved}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "accomplishments_by_team",
            as: "accomplishments",
            type: "string",
          },
          { field: "risks", as: "risks_flagged", type: "string" },
          { field: "carryover_items", as: "carryover", type: "string" },
          { field: "metrics_summary", as: "metrics_summary", type: "string" },
        ],
      ),
      n("5", "runCode", "Format report by team section with metrics", 1000, {
        runCodeDetails: {
          code: "const totalCompleted = (inputs.asana_count || 0) + (inputs.jira_count || 0); const weekLabel = new Date().toISOString().slice(0, 10); output = { total_completed: totalCompleted, report_week: weekLabel, formatted_report: inputs.executive_summary + '\\n\\n' + inputs.accomplishments + '\\n\\nRisks: ' + inputs.risks_flagged + '\\n\\nCarryover: ' + inputs.carryover };",
          outputs: [
            { name: "total_completed", type: "number" },
            { name: "report_week", type: "string" },
            { name: "formatted_report", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send weekly status report to leadership",
        1240,
        "{leadership_emails}",
        "Weekly Status Report — Week of {report_week}",
        "Hi team,\n\nHere is this week's status report:\n\n{executive_summary}\n\nTotal items completed: {total_completed} (Asana: {asana_count}, Jira: {jira_count})\n\n{accomplishments}\n\nRisks & Blockers:\n{risks_flagged}\n\nCarryover Items:\n{carryover}\n\nMetrics:\n{metrics_summary}",
      ),
      slackNode(
        "7",
        "Slack: Post weekly highlights to #team-updates",
        1480,
        ":bar_chart: *Weekly Status — {report_week}*\n\n{executive_summary}\n\nCompleted: {total_completed} items\n\nTop highlights:\n{accomplishments}\n\nRisks: {risks_flagged}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log weekly metrics for trend analysis",
        1720,
        "WeeklyMetrics",
        '[["{report_week}","{total_completed}","{asana_count}","{jira_count}","{risks_flagged}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Compiles a weekly status report every Friday by pulling completed tasks from Asana and resolved issues from Jira, using AI to generate an executive summary grouped by team with risks and carryover, distributing via email and Slack, and logging metrics to Google Sheets for trend analysis.",
  },

  // ─── 149. Executive Meeting Brief ─────────────────────────────────────

  "Executive Meeting Brief": {
    nodes: [
      trigger(
        "Google Calendar webhook: Executive meeting approaching",
        "webhook_app",
        {
          source: "google_calendar",
          event: "event.upcoming",
          howItStarts:
            "Fires when an executive meeting on the designated calendar is approaching (2 hours before start), providing meeting title, attendees, time, and any agenda notes.",
          setup:
            "Set up Google Calendar push notifications for the executive calendar. Filter for events tagged 'exec' or in the executive meeting series.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch pipeline data and key deal updates",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,dealstage,closedate,pipeline&limit=50&sorts=-hs_lastmodifieddate",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              { name: "results", customName: "pipeline_deals", type: "object" },
              { name: "total", customName: "total_deals", type: "number" },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compile KPIs from pipeline and operational data",
        520,
        {
          runCodeDetails: {
            code: "const deals = inputs.pipeline_deals || []; const totalPipeline = deals.reduce((sum, d) => sum + (parseFloat(d.properties?.amount) || 0), 0); const closingThisMonth = deals.filter(d => { const cd = d.properties?.closedate; return cd && new Date(cd).getMonth() === new Date().getMonth(); }); const closingValue = closingThisMonth.reduce((sum, d) => sum + (parseFloat(d.properties?.amount) || 0), 0); output = { total_pipeline_value: totalPipeline, closing_this_month: closingValue, deal_count: deals.length, closing_deal_count: closingThisMonth.length };",
            outputs: [
              { name: "total_pipeline_value", type: "number" },
              { name: "closing_this_month", type: "number" },
              { name: "deal_count", type: "number" },
              { name: "closing_deal_count", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Generate executive briefing document",
        760,
        "You are an executive briefing analyst. Create a concise 1-page executive meeting brief with sections: Pipeline Overview, Key Deals Update, Risks & Opportunities, Recommended Discussion Topics. Use bullet points and bold headers. Include specific numbers and actionable insights. Return brief_content, top_risks, discussion_topics, one_liner.",
        "Generate an executive meeting brief. Pipeline value: ${total_pipeline_value}. Deals closing this month: {closing_deal_count} worth ${closing_this_month}. Total deals: {deal_count}. Deal details: {pipeline_deals}",
        [
          { field: "brief_content", as: "briefing_doc", type: "string" },
          { field: "top_risks", as: "top_risks", type: "string" },
          {
            field: "discussion_topics",
            as: "discussion_topics",
            type: "string",
          },
          { field: "one_liner", as: "one_liner_summary", type: "string" },
        ],
      ),
      n("5", "hitApi", "Google Docs: Create briefing document", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Exec Brief — {meeting_title} — {today_date}",
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send briefing to executive attendees",
        1240,
        "{exec_attendee_emails}",
        "Meeting Brief: {meeting_title} — {today_date}",
        "Hi,\n\nPlease find the briefing document for today's meeting:\n\n{one_liner_summary}\n\nPipeline: ${total_pipeline_value} total | ${closing_this_month} closing this month ({closing_deal_count} deals)\n\nTop Risks:\n{top_risks}\n\nDiscussion Topics:\n{discussion_topics}\n\nFull brief: https://docs.google.com/document/d/{doc_id}/edit",
      ),
      slackNode(
        "7",
        "Slack: Notify EA that briefing is ready",
        1480,
        ":briefcase: *Exec Brief Ready*\n{meeting_title} — {today_date}\n\n{one_liner_summary}\n\nDoc: https://docs.google.com/document/d/{doc_id}/edit\nSent to: {exec_attendee_emails}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log briefing metrics",
        1720,
        "ExecBriefs",
        '[["{today_date}","{meeting_title}","{total_pipeline_value}","{closing_this_month}","{deal_count}","{doc_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Prepares executive meeting briefs by fetching HubSpot pipeline data, computing KPIs, using AI to generate a structured briefing with risks and discussion topics, creating a Google Doc, emailing attendees, notifying the EA via Slack, and logging briefing metrics.",
  },

  // ─── 150. Internal Announcement Approval ──────────────────────────────

  "Internal Announcement Approval": {
    nodes: [
      trigger(
        "Webhook form: Internal announcement draft submitted",
        "webhook_form",
        {
          howItStarts:
            "Fires when a team member submits an internal announcement draft through the company announcement form, providing the title, body, target audience, urgency, and author details.",
          setup:
            "Create an internal announcement submission form. Configure the webhook to capture title, body, audience scope, urgency level, and author information.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Check tone, compliance, and formatting",
        280,
        "You are a corporate communications editor. Review the announcement draft for professional tone, factual accuracy, policy compliance, inclusive language, and formatting. Flag any issues. Return tone_score, compliance_issues, formatting_suggestions, revised_draft, is_ready.",
        "Review this internal announcement draft for tone and compliance: Title: {announcement_title}. Body: {announcement_body}. Audience: {target_audience}",
        [
          { field: "tone_score", as: "tone_score", type: "number" },
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          {
            field: "formatting_suggestions",
            as: "format_suggestions",
            type: "string",
          },
          { field: "revised_draft", as: "revised_draft", type: "string" },
          { field: "is_ready", as: "is_publish_ready", type: "boolean" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Route by audience scope to appropriate approvers",
        520,
        {
          runCodeDetails: {
            code: "const audience = inputs.target_audience.toLowerCase(); let approverGroup = 'team_lead'; if (audience.includes('company') || audience.includes('all')) approverGroup = 'leadership'; else if (audience.includes('department')) approverGroup = 'department_head'; output = { approver_group: approverGroup, is_company_wide: audience.includes('company') || audience.includes('all'), routing_reason: 'Audience: ' + inputs.target_audience + ' -> Approver: ' + approverGroup };",
            outputs: [
              { name: "approver_group", type: "string" },
              { name: "is_company_wide", type: "boolean" },
              { name: "routing_reason", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "approvalGate",
        "Leadership: Approve announcement for distribution",
        760,
        {
          approvalGateDetails: {
            approvers: ["{approver_email}"],
            message:
              "Please review this internal announcement:\n\nTitle: {announcement_title}\nAudience: {target_audience}\nTone Score: {tone_score}/100\n\nDraft:\n{revised_draft}\n\nCompliance Notes: {compliance_issues}",
          },
        },
      ),
      n("5", "condition", "Check: Announcement approved?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{approval_status}",
              operator: "==",
              value: "approved",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Post approved announcement to target channel",
        1240,
        ":mega: *{announcement_title}*\n\n{revised_draft}\n\n— {author_name}",
      ),
      emailNode(
        "7",
        "Email: Distribute announcement to target audience",
        1480,
        "{audience_email_list}",
        "{announcement_title}",
        "{revised_draft}\n\n— {author_name}\n\nThis announcement was sent to: {target_audience}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log announcement approval and distribution",
        1720,
        "Announcements",
        '[["{today_date}","{announcement_title}","{author_name}","{target_audience}","{tone_score}","{approval_status}","{approver_group}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes internal announcement submissions through an AI tone and compliance check, routes to the appropriate approver based on audience scope, gates distribution behind leadership approval, posts to Slack and emails the target audience upon approval, and logs the announcement lifecycle.",
  },

  // ─── 151. Event Feedback from Slack to Database ───────────────────────

  "Event Feedback from Slack to Database": {
    nodes: [
      trigger(
        "Slack webhook: Feedback emoji or thread reply in event channel",
        "webhook_app",
        {
          source: "slack",
          event: "reaction_added",
          howItStarts:
            "Fires when a feedback emoji (e.g., :memo:, :bulb:, :warning:) is added to a message in an event channel or a thread reply contains feedback keywords.",
          setup:
            "Configure Slack event subscriptions for reaction_added in event channels. Define feedback emojis (:memo: for suggestion, :bulb: for idea, :warning: for issue).",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract feedback content and sentiment from Slack",
        280,
        "You are an event feedback analyst. Extract the feedback text, sentiment (positive/neutral/negative), category (logistics/content/speaker/venue/tech), actionability (actionable/informational), suggested improvement if any, and the person who gave feedback. Return feedback_text, sentiment, category, actionability, improvement, author.",
        "Analyze this Slack event feedback: {webhook_payload}",
        [
          { field: "feedback_text", as: "feedback_text", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "category", as: "feedback_category", type: "string" },
          { field: "actionability", as: "actionability", type: "string" },
          { field: "improvement", as: "suggested_improvement", type: "string" },
          { field: "author", as: "feedback_author", type: "string" },
        ],
      ),
      n("3", "runCode", "Categorize feedback type and assign priority", 520, {
        runCodeDetails: {
          code: "const sentiment = inputs.sentiment.toLowerCase(); const actionable = inputs.actionability === 'actionable'; let priority = 'low'; if (sentiment === 'negative' && actionable) priority = 'high'; else if (actionable) priority = 'medium'; output = { priority: priority, is_actionable: actionable, needs_followup: priority === 'high' };",
          outputs: [
            { name: "priority", type: "string" },
            { name: "is_actionable", type: "boolean" },
            { name: "needs_followup", type: "boolean" },
          ],
        },
      }),
      n(
        "4",
        "hitApi",
        "Airtable: Create feedback record in event database",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{airtable_base_id}/EventFeedback",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "records",
                value:
                  '[{"fields":{"Feedback":"{feedback_text}","Sentiment":"{sentiment}","Category":"{feedback_category}","Priority":"{priority}","Author":"{feedback_author}","Improvement":"{suggested_improvement}","Date":"{today_date}","Source":"Slack"}}]',
              },
            ],
            variables: [
              {
                name: "records[0].id",
                customName: "airtable_record_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Is feedback actionable and needs follow-up?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_followup}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Reply in thread acknowledging feedback",
        1240,
        ":white_check_mark: Thanks for the feedback, {feedback_author}! This has been logged as *{feedback_category}* ({priority} priority).\n\n{suggested_improvement}\n\nWe'll follow up on actionable items.",
      ),
      n(
        "7",
        "hitApi",
        "Asana: Create follow-up task for actionable feedback",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"Event Feedback: {feedback_category} — {priority}","notes":"Feedback: {feedback_text}\\nAuthor: {feedback_author}\\nSuggested Improvement: {suggested_improvement}\\nAirtable: {airtable_record_id}","projects":["{events_project_id}"]}',
              },
            ],
            variables: [
              { name: "data.gid", customName: "asana_task_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log feedback for event reporting",
        1720,
        "EventFeedback",
        '[["{today_date}","{feedback_author}","{feedback_category}","{sentiment}","{priority}","{is_actionable}","{airtable_record_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures event feedback from Slack emoji reactions and threads, uses AI to extract sentiment and categorize, stores in Airtable, creates Asana follow-up tasks for actionable negative feedback, acknowledges in the Slack thread, and logs to Google Sheets for event reporting.",
  },

  // ─── 152. Form Submission to Team Channel Alert ───────────────────────

  "Form Submission to Team Channel Alert": {
    nodes: [
      trigger("Webhook form: New submission received", "webhook_form", {
        howItStarts:
          "Fires when a new form submission is received from the website or internal tool, providing all form field values, submission timestamp, and source URL.",
        setup:
          "Connect your form tool (Outgrow Forms, Outgrow Forms, or custom) to send webhook payloads on submission. Include all field values and metadata.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract key fields and determine routing",
        280,
        "You are a form triage assistant. Extract the submitter name, email, subject/topic, message body, urgency indicators, and form type from the submission. Determine which team should handle it based on content. Return submitter_name, email, subject, message, urgency, form_type, target_team, target_channel.",
        "Analyze this form submission and determine routing: {webhook_payload}",
        [
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "email", as: "submitter_email", type: "string" },
          { field: "subject", as: "submission_subject", type: "string" },
          { field: "message", as: "submission_message", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
          { field: "target_team", as: "target_team", type: "string" },
          { field: "target_channel", as: "target_channel", type: "string" },
        ],
      ),
      n("3", "runCode", "Route to correct Slack channel based on team", 520, {
        runCodeDetails: {
          code: "const channelMap = { 'sales': '#sales-inquiries', 'support': '#support-triage', 'engineering': '#eng-requests', 'marketing': '#marketing-inbox', 'hr': '#hr-requests' }; const team = inputs.target_team.toLowerCase(); const channel = channelMap[team] || '#general-inbox'; const isUrgent = inputs.urgency_level.toLowerCase().includes('urgent') || inputs.urgency_level.toLowerCase().includes('high'); output = { slack_channel: channel, is_urgent: isUrgent, routing_label: team + ' -> ' + channel };",
          outputs: [
            { name: "slack_channel", type: "string" },
            { name: "is_urgent", type: "boolean" },
            { name: "routing_label", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Slack: Post formatted submission alert to team channel",
        760,
        ":incoming_envelope: *New Form Submission*\n*Subject:* {submission_subject}\n*From:* {submitter_name} ({submitter_email})\n*Urgency:* {urgency_level}\n*Team:* {target_team}\n\n{submission_message}",
      ),
      n("5", "condition", "Check: Is this submission urgent?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{is_urgent}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: DM team lead for urgent submissions",
        1240,
        ":rotating_light: *URGENT Form Submission*\n*{submission_subject}*\nFrom: {submitter_name} ({submitter_email})\n\n{submission_message}\n\nPlease respond within 1 hour. Routed to {slack_channel}.",
      ),
      n("7", "hitApi", "Airtable: Log submission with routing details", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/FormSubmissions",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Name":"{submitter_name}","Email":"{submitter_email}","Subject":"{submission_subject}","Message":"{submission_message}","Team":"{target_team}","Urgency":"{urgency_level}","Date":"{today_date}"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_id",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send backup notification to team inbox",
        1720,
        "{team_inbox_email}",
        "Form Submission: {submission_subject} — {urgency_level}",
        "New form submission received:\n\nFrom: {submitter_name} ({submitter_email})\nSubject: {submission_subject}\nUrgency: {urgency_level}\nRouted to: {target_team} ({slack_channel})\n\nMessage:\n{submission_message}\n\nAirtable record: {airtable_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives form submissions, uses AI to extract fields and determine team routing, posts a formatted alert to the appropriate Slack channel, DMs the team lead for urgent submissions, logs to Airtable, and sends a backup email notification to the team inbox.",
  },

  // ─── 153. Daily Worklog to Spreadsheet ────────────────────────────────

  "Daily Worklog to Spreadsheet": {
    nodes: [
      trigger("Schedule: Collect daily work logs at 6 PM", "schedule", {
        schedule: "Every day at 6:00 PM",
        howItStarts:
          "Fires daily at 6 PM to fetch time tracking entries from Toggl/Clockify for the current day, normalize the data, and append to a tracking spreadsheet.",
        setup:
          "Configure your Toggl or Clockify API token and workspace ID. Set the Google Sheets spreadsheet ID for the time tracking destination.",
        inputFields: [],
      }),
      n("2", "hitApi", "Toggl: Fetch today's time entries for workspace", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.track.toggl.com/api/v9/me/time_entries?start_date={today_start}&end_date={today_end}",
          headers: [
            { key: "Authorization", value: "Basic {toggl_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "length", customName: "entry_count", type: "number" },
            { name: "", customName: "time_entries", type: "object" },
          ],
        },
      }),
      n("3", "runCode", "Normalize and categorize time entries", 520, {
        runCodeDetails: {
          code: "const entries = inputs.time_entries || []; const normalized = entries.map(e => { const dur = Math.round((e.duration || 0) / 3600 * 100) / 100; const cat = (e.tags || []).includes('meeting') ? 'Meeting' : (e.tags || []).includes('development') ? 'Development' : (e.tags || []).includes('admin') ? 'Admin' : 'Other'; return { description: e.description || 'Untitled', project: e.project_name || 'No Project', hours: dur, category: cat, date: new Date(e.start).toISOString().slice(0, 10) }; }); const totalHours = normalized.reduce((sum, e) => sum + e.hours, 0); output = { normalized_entries: JSON.stringify(normalized), total_hours: Math.round(totalHours * 100) / 100, category_breakdown: JSON.stringify(normalized.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + e.hours; return acc; }, {})) };",
          outputs: [
            { name: "normalized_entries", type: "string" },
            { name: "total_hours", type: "number" },
            { name: "category_breakdown", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "4",
        "Google Sheets: Append normalized time entries as rows",
        760,
        "Worklog",
        "{normalized_entries}",
      ),
      aiNode(
        "5",
        "OpenAI: Generate time summary and productivity insights",
        1000,
        "You are a productivity analyst. Analyze the day's time entries and provide a summary of how time was spent, note any patterns (too many meetings, insufficient focus time), and suggest improvements. Return daily_summary, focus_hours, meeting_hours, productivity_score, recommendation.",
        "Analyze today's worklog: Total hours: {total_hours}. Categories: {category_breakdown}. Entries: {normalized_entries}",
        [
          { field: "daily_summary", as: "daily_summary", type: "string" },
          { field: "focus_hours", as: "focus_hours", type: "number" },
          { field: "meeting_hours", as: "meeting_hours", type: "number" },
          {
            field: "productivity_score",
            as: "productivity_score",
            type: "number",
          },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("6", "condition", "Check: Total hours below 6 (low log day)?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{total_hours}", operator: "<", value: "6" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Send low-hours reminder or daily summary",
        1480,
        ":clock6: *Daily Time Summary*\nTotal: {total_hours}h | Focus: {focus_hours}h | Meetings: {meeting_hours}h\nProductivity Score: {productivity_score}/100\n\n{daily_summary}\n\nRecommendation: {recommendation}",
      ),
      emailNode(
        "8",
        "Email: Weekly time summary compilation",
        1720,
        "{user_email}",
        "Daily Worklog Summary — {today_date}",
        "Hi,\n\nHere is your daily time tracking summary:\n\nTotal hours logged: {total_hours}h\nFocus time: {focus_hours}h\nMeeting time: {meeting_hours}h\nProductivity Score: {productivity_score}/100\n\n{daily_summary}\n\nRecommendation: {recommendation}\n\nCategory breakdown: {category_breakdown}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Fetches daily time entries from Toggl at 6 PM, normalizes and categorizes them, appends to Google Sheets, uses AI to generate productivity insights and a time summary, alerts via Slack for low-hour days, and emails a daily worklog summary with recommendations.",
  },
};
